import { artistInitialState } from './initial-state';
import { ArtistActions } from './action-creators';
import { ArtistState } from './state';
import * as actions from './actions';
import { blankArtistPage } from './blank-artist-page';

const artistReducer = (
  state: ArtistState = artistInitialState,
  action: ArtistActions
): ArtistState => {
  switch (action.type) {
    case actions.GET_ARTISTS_SUCCESS:
      return {
        ...state,
        myArtists: action.artists
          .filter(
            (artist, index, arr) =>
              index === arr.findIndex((o) => o.id === artist.id)
          )
          .sort((a, b) => {
            if ((a.lastAccess || 0) < (b.lastAccess || 0)) {
              return 1;
            }
            if ((a.lastAccess || 0) > (b.lastAccess || 0)) {
              return -1;
            }

            return 0;
          }),
        loaded: true,
      };

    case actions.DEACTIVATE_ARTIST_SUCCESS:
      return {
        ...state,
        active: undefined,
      };

    case actions.UPDATE_ARTIST_SUCCESS: {
      return {
        ...state,
        active: { ...action.artist },
        myArtists: state.myArtists
          .filter(
            (artist, index, arr) =>
              index === arr.findIndex((o) => o.id === artist.id)
          )
          .map((a) => {
            if (a.id === action.artist.id) {
              return { ...action.artist };
            }

            return a;
          }),
      };
    }

    case actions.UPDATE_ARTIST_DATA: {
      return {
        ...state,
        active:
          action.id === state.active?.id
            ? { ...state.active, ...action.artist }
            : state.active,
        myArtists: state.myArtists.map((a) => {
          if (a.id === action.id) {
            return { ...a, ...action.artist };
          }
          return a;
        }),
      };
    }

    case actions.GET_ARTIST_TEAM_SUCCESS:
      return {
        ...state,
        activeTeam: action.team,
      };

    case actions.SWITCH_TEAM_MEMBER_ROLE_SUCCESS:
      return {
        ...state,
        activeTeam: state.activeTeam?.map((member) => {
          if (member.id === action.teamMemberId) {
            return { ...member, role: action.role };
          }
          return member;
        }),
      };

    case actions.SEARCH_ARTISTS:
      return {
        ...state,
        search: [],
      };

    case actions.SEARCH_ARTISTS_SUCCESS:
      return {
        ...state,
        search: action.artists,
      };

    case actions.SEARCH_ARTISTS_RESET:
      return {
        ...state,
        search: [],
      };

    case actions.GET_ACHIEVEMENTS_SUCCESS:
      return {
        ...state,
        achievements: action.achievements,
      };

    case actions.SEARCH_EXTERNAL_ARTISTS_SUCCESS:
      return {
        ...state,
        externalIds: action.results,
      };

    case actions.SEARCH_EXTERNAL_ARTISTS_RESET:
      return {
        ...state,
        externalIds: undefined,
      };

    case actions.SET_ACTIVE_ARTIST:
      return {
        ...state,
        active: { ...action.artist, lastAccess: new Date().getTime() },
        myArtists: state.myArtists
          .map((a) => {
            if (a.id === action.artist.id) {
              return { ...a, lastAccess: new Date().getTime() };
            }
            return a;
          })
          .sort((a, b) => {
            if ((a.lastAccess || 0) < (b.lastAccess || 0)) {
              return 1;
            }
            if ((a.lastAccess || 0) > (b.lastAccess || 0)) {
              return -1;
            }

            return 0;
          }),
        artistPage: blankArtistPage(),
      };

    case actions.UPDATE_PAGE_FIELD: {
      return {
        ...state,
        artistPage: {
          ...state.artistPage,
          artist: state.artistPage.artist
            ? { ...state.artistPage.artist }
            : state.active
            ? { ...state.active }
            : undefined,
          [action.field]: action.value,
        },
      };
    }

    case actions.UPDATE_PAGE_SECTION_FIELD: {
      if (action.section === 'subdomain') {
        return state;
      }

      return {
        ...state,
        artistPage: {
          ...state.artistPage,
          artist: state.artistPage.artist
            ? { ...state.artistPage.artist }
            : state.active
            ? { ...state.active }
            : undefined,
          [action.section]: {
            ...state.artistPage[action.section],
            [action.field]: action.value,
          },
        },
      };
    }

    case actions.REMOVE_PROFILE_IMAGE: {
      return {
        ...state,
        artistPage: {
          ...state.artistPage,
          images: state.artistPage.images.filter((i) => i.id !== action.id),
          home: {
            ...state.artistPage.home,
            background:
              state.artistPage.home.background?.id === action.id
                ? undefined
                : state.artistPage.home.background,
          },
          music: {
            ...state.artistPage.music,
            background:
              state.artistPage.music.background?.id === action.id
                ? undefined
                : state.artistPage.music.background,
          },
          contact: {
            ...state.artistPage.contact,
            background:
              state.artistPage.contact.background?.id === action.id
                ? undefined
                : state.artistPage.contact.background,
          },
        },
      };
    }

    case actions.GET_ARTIST_PAGE_SUCCESS:
      const artistPage = {
        ...state.artistPage,
        ...action.page,
      };

      return {
        ...state,
        artistPage: {
          ...artistPage,
          id: artistPage.id ? artistPage.id : (state.active?.shortId as string),
        },
      };

    case actions.GET_ARTIST_PAGE_ERROR:
      return {
        ...state,
        artistPage: {
          ...artistInitialState.artistPage,
          id: state.active?.shortId as string,
        },
      };

    case actions.RESET_ARTIST_STORE: {
      return {
        ...artistInitialState,
      };
    }

    case actions.UPDATE_ARTISTS_PLATFORMS_SUCCESS: {
      const exists = action.artists.find((a) => a.id === state.active?.id);

      if (exists?.platforms && state.active) {
        return {
          ...state,
          active: {
            ...state.active,
            platforms: {
              ...state.active?.platforms,
              ...exists.platforms,
            },
          },
        };
      }

      return state;
    }

    case actions.VALIDATE_EXTERNAL_ARTIST_ID_SUCCESS: {
      return {
        ...state,
        externalIdValidation: {
          externalId: action.externalId,
          validationResult: action.validationResult,
        },
      };
    }

    case actions.VALIDATE_EXTERNAL_ARTIST_ID_ERROR: {
      return {
        ...state,
        externalIdValidation: {
          externalId: action.externalId,
          validationResult: false,
        },
      };
    }

    default:
      return state;
  }
};

export default artistReducer;
